import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { apiService } from '../services/apiService';
import { fetchProfile } from '../store/slices/profileSlice';
import { logoutUser } from '../store/slices/authSlice';
import { branding } from '../constants/branding';
import QRCode from 'qrcode';

const cryptoChainsMatch = (chainHint: string, walletBlockchain: string): boolean => {
  const a = (chainHint || '').trim().toLowerCase();
  const b = (walletBlockchain || '').trim().toLowerCase();
  if (!a || !b) return false;
  if (a === b) return true;
  const norm = (c: string) =>
    c === 'eth' || c === 'ethereum'
      ? 'ethereum'
      : c === 'matic' || c === 'polygon'
        ? 'polygon'
        : c === 'btc' || c === 'bitcoin'
          ? 'bitcoin'
          : c;
  return norm(a) === norm(b);
};

function retailHasAccess(status: string | undefined): boolean {
  return status === 'ACTIVE' || status === 'GRACE_PERIOD';
}

const RetailSubscriptionRenewalPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const adminUser = useAppSelector((s) => s.auth.adminUser);

  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<Record<string, unknown> | null>(null);
  const [pendingUpgradeInfo, setPendingUpgradeInfo] = useState<any>(null);
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewError, setRenewError] = useState<string | null>(null);
  const [plans, setPlans] = useState<
    Array<{ id: number; name: string; priceMonthly: number; currency: string; paymentMethods: 'ALL' | 'STRIPE' | 'CRYPTO' }>
  >([]);
  const [selectedNonePlanId, setSelectedNonePlanId] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'STRIPE' | 'CRYPTO'>('STRIPE');
  const [cryptoBlockchain, setCryptoBlockchain] = useState('ethereum');
  const [cryptoSymbol, setCryptoSymbol] = useState('USDT');
  const [cryptoTxHash, setCryptoTxHash] = useState('');
  const [cryptoSubmitBusy, setCryptoSubmitBusy] = useState(false);
  const [cryptoWalletPayBusy, setCryptoWalletPayBusy] = useState(false);
  const [destQr, setDestQr] = useState<string | null>(null);
  const [userWallet, setUserWallet] = useState<{ address: string; blockchain: string } | null>(null);
  const [userWalletQr, setUserWalletQr] = useState<string | null>(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    const response = await apiService.get<Record<string, unknown>>('/api/retail/subscription/status');
    if (response.success && response.data) {
      setSubscriptionStatus(response.data as Record<string, unknown>);
    }
  }, []);

  const loadPendingUpgrade = useCallback(async () => {
    try {
      const res = await apiService.get('/api/retail/subscription/upgrade/pending');
      if (res.success && res.data) {
        setPendingUpgradeInfo(res.data as any);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || adminUser) {
      navigate('/login', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const stRes = await apiService.get<Record<string, unknown>>('/api/retail/subscription/status');
        let data = stRes.success && stRes.data ? (stRes.data as Record<string, unknown>) : null;
        if (cancelled) return;
        // API error / empty body: still show renewal shell (same as status NONE) instead of infinite "loading"
        if (!data) {
          data = {
            status: 'NONE',
            message: 'We could not load your subscription details. You can still choose a plan and renew below.',
          };
        }
        if (retailHasAccess(data.status as string | undefined)) {
          await dispatch(fetchProfile());
          navigate('/dashboard', { replace: true });
          return;
        }
        setSubscriptionStatus(data);
        await loadPendingUpgrade();
        const res = await apiService.get<any[]>('/api/retail/subscription/plans');
        if (cancelled) return;
        if (res.success && Array.isArray(res.data)) {
          const mapped = (res.data as any[])
            .map((p) => ({
              id: Number(p.id),
              name: String(p.name ?? 'Plan'),
              priceMonthly: parseFloat(String(p.priceMonthly ?? p.price_monthly ?? '0').replace(',', '.')) || 0,
              currency: (p.currency as string) || 'EUR',
              paymentMethods: String(p.registrationPaymentMethods || p.registration_payment_methods || 'ALL').toUpperCase() as
                | 'ALL'
                | 'STRIPE'
                | 'CRYPTO',
              active: p.is_active !== false && p.active !== false,
            }))
            .filter((p) => p.active && Number.isFinite(p.id));
          setPlans(mapped);
        }
      } catch {
        if (!cancelled) {
          setSubscriptionStatus({
            status: 'NONE',
            message: 'Something went wrong while loading your subscription. You can try again below or contact support.',
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, adminUser, navigate, dispatch, fetchSubscriptionStatus, loadPendingUpgrade]);

  useEffect(() => {
    const stripe = (searchParams.get('stripe') || '').toLowerCase();
    const upgrade = searchParams.get('upgrade');
    if (stripe !== 'success' || upgrade !== '1' || !isAuthenticated) return;
    const sessionId = searchParams.get('session_id');
    void (async () => {
      try {
        await apiService.post('/api/retail/subscription/upgrade/confirm-payment', sessionId ? { sessionId } : {});
      } catch {
        /* webhook may apply */
      }
      await loadPendingUpgrade();
      await fetchSubscriptionStatus();
      if (window.history.replaceState) {
        window.history.replaceState({}, '', '/renew-subscription');
      }
    })();
  }, [searchParams, isAuthenticated, loadPendingUpgrade, fetchSubscriptionStatus]);

  useEffect(() => {
    let cancelled = false;
    const p = pendingUpgradeInfo;
    if (!p?.hasPending || p.status !== 'AWAITING_PAYMENT' || String(p.paymentMethod || '').toUpperCase() !== 'CRYPTO') {
      setDestQr(null);
      setUserWallet(null);
      setUserWalletQr(null);
      return;
    }
    const chainHint = String(p.blockchain || (p as any).cryptoBlockchain || '').trim();
    const dest = String(p.destinationAddress || (p as any).cryptoDestinationAddress || '').trim();
    void (async () => {
      try {
        if (dest) {
          const dqr = await QRCode.toDataURL(dest, { width: 180, margin: 2, errorCorrectionLevel: 'M' });
          if (!cancelled) setDestQr(dqr);
        }
      } catch {
        if (!cancelled) setDestQr(null);
      }
      try {
        const wres = await apiService.get<Array<{ blockchain?: string; address?: string }>>('/crypto/wallet');
        if (cancelled) return;
        const list = Array.isArray(wres.data) ? wres.data : [];
        const match = list.find((w) => cryptoChainsMatch(chainHint, String(w.blockchain || '')));
        if (match?.address) {
          setUserWallet({ address: match.address, blockchain: String(match.blockchain || chainHint) });
          try {
            const uqr = await QRCode.toDataURL(match.address, { width: 180, margin: 2, errorCorrectionLevel: 'M' });
            if (!cancelled) setUserWalletQr(uqr);
          } catch {
            if (!cancelled) setUserWalletQr(null);
          }
        } else {
          setUserWallet(null);
          setUserWalletQr(null);
        }
      } catch {
        if (!cancelled) {
          setUserWallet(null);
          setUserWalletQr(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingUpgradeInfo]);

  const buildPaymentMethodPayload = () => {
    if (paymentMethod === 'CRYPTO') {
      return { paymentMethod: 'CRYPTO', blockchain: cryptoBlockchain, symbol: cryptoSymbol };
    }
    return { paymentMethod: 'STRIPE' };
  };

  const currentPlanId = subscriptionStatus?.planId != null ? Number(subscriptionStatus.planId) : null;
  const selectedPlanForRenewal =
    (currentPlanId != null && Number.isFinite(currentPlanId) ? plans.find((p) => p.id === currentPlanId) : undefined) ||
    (selectedNonePlanId != null && Number.isFinite(selectedNonePlanId) ? plans.find((p) => p.id === selectedNonePlanId) : undefined) ||
    null;
  const selectedPlanPaymentMethods: 'ALL' | 'STRIPE' | 'CRYPTO' =
    selectedPlanForRenewal?.paymentMethods || 'ALL';
  const allowedPaymentMethods: Array<'STRIPE' | 'CRYPTO'> =
    selectedPlanPaymentMethods === 'STRIPE'
      ? ['STRIPE']
      : selectedPlanPaymentMethods === 'CRYPTO'
        ? ['CRYPTO']
        : ['STRIPE', 'CRYPTO'];

  useEffect(() => {
    if (allowedPaymentMethods.includes(paymentMethod)) return;
    setPaymentMethod(allowedPaymentMethods[0] || 'STRIPE');
  }, [allowedPaymentMethods, paymentMethod]);

  const startRenewalFlow = async () => {
    const status = subscriptionStatus?.status as string | undefined;
    let planId: number | null = subscriptionStatus?.planId != null ? Number(subscriptionStatus.planId) : null;
    if (status === 'NONE' || planId == null || !Number.isFinite(planId)) {
      if (selectedNonePlanId == null || !Number.isFinite(selectedNonePlanId)) {
        setRenewError('Please select a plan.');
        return;
      }
      planId = selectedNonePlanId;
    }
    setRenewBusy(true);
    setRenewError(null);
    try {
      const res = await apiService.post('/api/retail/subscription/renew/start', {
        planId,
        ...buildPaymentMethodPayload(),
      });
      if (!res.success) {
        setRenewError((res as any).error || 'Renewal could not be started.');
        return;
      }
      const data = res.data as any;
      if (data?.requiresCompliance) {
        await loadPendingUpgrade();
        await fetchSubscriptionStatus();
      } else if (data?.checkoutUrl) {
        window.open(data.checkoutUrl as string, '_blank', 'noopener,noreferrer');
        await loadPendingUpgrade();
        await fetchSubscriptionStatus();
      } else if (data?.paymentMethod === 'CRYPTO') {
        await loadPendingUpgrade();
        await fetchSubscriptionStatus();
      }
    } catch (err: any) {
      setRenewError(err?.message || 'Renewal could not be started.');
    } finally {
      setRenewBusy(false);
    }
  };

  const submitCryptoPaymentProof = async () => {
    const upgradeRequestId = pendingUpgradeInfo?.upgradeRequestId ?? pendingUpgradeInfo?.requestId;
    if (!upgradeRequestId || !cryptoTxHash.trim()) return;
    setCryptoSubmitBusy(true);
    setRenewError(null);
    try {
      const res = await apiService.post('/api/retail/subscription/upgrade/submit-crypto-payment', {
        upgradeRequestId,
        txHash: cryptoTxHash.trim(),
      });
      if (!res.success) {
        setRenewError((res as any).error || 'Could not submit transaction hash.');
        return;
      }
      setCryptoTxHash('');
      await loadPendingUpgrade();
      await fetchSubscriptionStatus();
    } catch (err: any) {
      setRenewError(err?.message || 'Could not submit transaction hash.');
    } finally {
      setCryptoSubmitBusy(false);
    }
  };

  const payWithIntegratedWallet = async () => {
    const upgradeRequestId = pendingUpgradeInfo?.upgradeRequestId ?? pendingUpgradeInfo?.requestId;
    if (!upgradeRequestId) return;
    setCryptoWalletPayBusy(true);
    setRenewError(null);
    try {
      const res = await apiService.post('/api/retail/subscription/upgrade/pay-with-wallet', { upgradeRequestId });
      if (!res.success) {
        setRenewError((res as any).error || 'Wallet payment failed.');
        return;
      }
      await loadPendingUpgrade();
      await fetchSubscriptionStatus();
    } catch (err: any) {
      setRenewError(err?.message || 'Wallet payment failed.');
    } finally {
      setCryptoWalletPayBusy(false);
    }
  };

  const handleLogout = () => {
    void dispatch(logoutUser());
    navigate('/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zazoo-light-grey">
        <p className="font-nunito text-gray-600">Loading subscription…</p>
      </div>
    );
  }

  if (!subscriptionStatus) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-zazoo-light-grey px-6">
        <p className="font-nunito text-gray-700 text-center max-w-md">
          Subscription information could not be loaded. Please try again or log out and sign back in.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-6 py-2.5 bg-zazoo-accent-blue text-white rounded-full font-mukta text-sm font-medium"
        >
          Retry
        </button>
        <button
          type="button"
          onClick={handleLogout}
          className="text-sm font-mukta text-zazoo-accent-pink hover:opacity-90"
        >
          Log out
        </button>
      </div>
    );
  }

  const st = subscriptionStatus.status as string | undefined;
  const planName = (subscriptionStatus.planName as string) || '—';
  const checkoutUrl = pendingUpgradeInfo?.checkoutUrl as string | undefined;
  const statusPillClass =
    st === 'ACTIVE' || st === 'GRACE_PERIOD'
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
      : st === 'NONE'
        ? 'bg-slate-100 text-slate-700 border-slate-200'
        : 'bg-amber-100 text-amber-700 border-amber-200';

  return (
    <div className="min-h-screen bg-zazoo-light-grey flex flex-col">
      <header className="bg-white border-b border-gray-200 px-6 py-4 md:px-8 flex justify-between items-center">
        <h1 className="text-2xl font-nunito font-bold text-gray-900">Renew your subscription</h1>
        <button
          type="button"
          onClick={handleLogout}
          className="px-4 py-2 rounded-full border border-gray-300 text-sm font-mukta font-semibold text-zazoo-accent-pink hover:bg-gray-50 transition-colors"
        >
          Log out
        </button>
      </header>
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 md:px-6 py-8 md:py-10 space-y-6">
        <p className="text-base font-nunito text-gray-700" style={{ fontFamily: branding.fontFamily }}>
          Your account access is limited until your subscription is active again. Complete renewal to restore full access.
        </p>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
          <>
            <div className="text-xs font-nunito font-semibold text-gray-500 uppercase tracking-wider">Subscription overview</div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-mukta text-gray-500">Current plan</div>
                <div className="font-nunito text-2xl font-bold text-gray-900">{planName}</div>
              </div>
              <span className={`px-3 py-1 rounded-full border text-xs font-semibold font-mukta ${statusPillClass}`}>
                {st || 'Unknown'}
              </span>
            </div>
            {subscriptionStatus.message && (
              <p className="text-sm font-nunito text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                {String(subscriptionStatus.message)}
              </p>
            )}
          </>
        </div>

        {pendingUpgradeInfo?.hasPending && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 space-y-3">
            <div className="font-nunito text-lg font-bold text-blue-900">Payment in progress</div>
            <p className="text-sm text-blue-900 font-mukta">{pendingUpgradeInfo.message || 'Complete the pending step.'}</p>
            {pendingUpgradeInfo.status === 'AWAITING_PAYMENT' &&
              String(pendingUpgradeInfo.paymentMethod || '').toUpperCase() === 'STRIPE' &&
              checkoutUrl && (
                <a
                  href={checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-1 px-6 py-2.5 bg-zazoo-accent-blue text-white rounded-full font-mukta text-sm font-semibold hover:opacity-95 transition-opacity"
                >
                  Open checkout
                </a>
              )}
            {pendingUpgradeInfo.status === 'AWAITING_PAYMENT' &&
              String(pendingUpgradeInfo.paymentMethod || '').toUpperCase() === 'CRYPTO' && (
                <div className="grid gap-4 mt-2">
                  <div className="text-xs text-blue-900 space-y-1 font-mukta">
                    <div>
                      <strong>Destination:</strong>{' '}
                      {String(pendingUpgradeInfo.destinationAddress || pendingUpgradeInfo.cryptoDestinationAddress || '—')}
                    </div>
                    {destQr && <img src={destQr} alt="Pay to" className="w-36 h-36 mt-2" />}
                  </div>
                  {userWallet && (
                    <div className="text-xs text-blue-900 border-t border-blue-200 pt-3 font-mukta">
                      <div>
                        <strong>Your wallet:</strong> {userWallet.address}
                      </div>
                      {userWalletQr && <img src={userWalletQr} alt="Your wallet" className="w-36 h-36 mt-2" />}
                      <button
                        type="button"
                        onClick={() => void payWithIntegratedWallet()}
                        disabled={cryptoWalletPayBusy}
                        className="mt-3 w-full py-2.5 rounded-full bg-zazoo-accent-blue text-white text-sm font-mukta font-semibold disabled:opacity-50"
                      >
                        {cryptoWalletPayBusy ? 'Processing…' : 'Pay with my Zogzila wallet'}
                      </button>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-nunito font-semibold text-blue-900 mb-1">Transaction hash</label>
                    <input
                      value={cryptoTxHash}
                      onChange={(e) => setCryptoTxHash(e.target.value)}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm font-mukta"
                      placeholder="0x…"
                    />
                    <button
                      type="button"
                      onClick={() => void submitCryptoPaymentProof()}
                      disabled={cryptoSubmitBusy || !cryptoTxHash.trim()}
                        className="mt-2 w-full py-2.5 rounded-full border border-blue-400 text-blue-900 text-sm font-mukta font-semibold disabled:opacity-50"
                    >
                      {cryptoSubmitBusy ? 'Submitting…' : 'Submit tx hash'}
                    </button>
                  </div>
                </div>
              )}
          </div>
        )}

        {!pendingUpgradeInfo?.hasPending && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-5">
            {(st === 'NONE' || subscriptionStatus.planId == null) && plans.length > 0 && (
              <div>
                <label className="block text-sm font-nunito font-semibold text-gray-700 mb-2">Choose a plan</label>
                <select
                  value={selectedNonePlanId ?? ''}
                  onChange={(e) => setSelectedNonePlanId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl font-mukta text-sm bg-white"
                >
                  <option value="">Select…</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.priceMonthly} {p.currency}/mo
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-nunito font-semibold text-gray-700 mb-2">Payment method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as 'STRIPE' | 'CRYPTO')}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl font-mukta text-sm bg-white"
              >
                {allowedPaymentMethods.includes('STRIPE') && <option value="STRIPE">Card (Stripe)</option>}
                {allowedPaymentMethods.includes('CRYPTO') && <option value="CRYPTO">Crypto</option>}
              </select>
            </div>
            {paymentMethod === 'CRYPTO' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-nunito font-semibold text-gray-600 mb-1">Network</label>
                  <select
                    value={cryptoBlockchain}
                    onChange={(e) => setCryptoBlockchain(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm font-mukta bg-white"
                  >
                    <option value="ethereum">Ethereum</option>
                    <option value="polygon">Polygon</option>
                    <option value="bitcoin">Bitcoin</option>
                    <option value="stellar">Stellar</option>
                    <option value="solana">Solana</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-nunito font-semibold text-gray-600 mb-1">Symbol</label>
                  <input
                    value={cryptoSymbol}
                    onChange={(e) => setCryptoSymbol(e.target.value.toUpperCase())}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm font-mukta"
                    placeholder="USDT"
                  />
                </div>
              </div>
            )}
            {renewError && <p className="text-sm text-red-600 font-nunito">{renewError}</p>}
            <div className="pt-1 md:flex md:justify-end">
              <button
                type="button"
                onClick={() => void startRenewalFlow()}
                disabled={renewBusy}
                className="px-8 py-3 rounded-full font-mukta font-semibold text-sm transition-opacity"
                style={{
                  width: '220px',
                  maxWidth: '100%',
                  backgroundColor: renewBusy ? '#d1d5db' : '#086afb',
                  color: renewBusy ? '#374151' : '#ffffff',
                  cursor: renewBusy ? 'not-allowed' : 'pointer',
                  opacity: renewBusy ? 1 : 0.98,
                }}
              >
                {renewBusy ? 'Starting…' : 'Start renewal'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RetailSubscriptionRenewalPage;
